import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, Truck, Ship, Plane, Globe, Shield, Zap, Users, ArrowRight, CheckCircle2, TrendingUp, Clock } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const networkHubs = [
  {
    name: 'West Coast Hub',
    location: 'Los Angeles, CA',
    image: 'assets/port-6670684_1280.jpg',
    services: ['CROSSDOCS', 'Ocean Freight', 'Air Cargo', 'Warehousing'],
    capacity: '500,000 sq ft',
    throughput: '10,000+ pallets/day',
    features: ['Port proximity', 'Temperature control', 'Customs clearance', '24/7 operations']
  },
  {
    name: 'Central Hub',
    location: 'Chicago, IL',
    image: 'assets/warehouse-8921538_1280.jpg',
    services: ['CROSSDOCS', 'Line-Haul', 'Warehousing', 'Distribution'],
    capacity: '750,000 sq ft',
    throughput: '15,000+ pallets/day',
    features: ['Rail access', 'Intermodal', 'Full automation', 'Strategic location']
  },
  {
    name: 'East Coast Hub',
    location: 'Newark, NJ',
    image: 'assets/ship-105596_1280.jpg',
    services: ['Ocean Freight', 'CROSSDOCS', 'Air Cargo', 'Last-Mile'],
    capacity: '400,000 sq ft',
    throughput: '8,000+ pallets/day',
    features: ['Port access', 'Airport proximity', 'Metro delivery', 'Customs expertise']
  },
  {
    name: 'Gulf Coast Hub',
    location: 'Houston, TX',
    image: 'assets/warehouse-9006780_1280.jpg',
    services: ['CROSSDOCS', 'Line-Haul', 'Warehousing', 'Energy Logistics'],
    capacity: '600,000 sq ft',
    throughput: '12,000+ pallets/day',
    features: ['Energy corridor', 'Port access', 'Rail connections', 'Specialized handling']
  }
];

const networkStats = [
  { label: 'Hub Locations', value: '25+', icon: MapPin },
  { label: 'States Covered', value: '48', icon: Globe },
  { label: 'Daily Throughput', value: '50K+', icon: TrendingUp },
  { label: 'Response Time', value: '<2hrs', icon: Clock }
];

const coverageAreas = [
  {
    region: 'Northeast',
    states: ['NY', 'NJ', 'PA', 'MA', 'CT', 'RI', 'VT', 'NH', 'ME'],
    coverage: '98%',
    image: 'assets/port-6670684_1280.jpg'
  },
  {
    region: 'Southeast',
    states: ['FL', 'GA', 'NC', 'SC', 'VA', 'WV', 'KY', 'TN', 'AL', 'MS', 'AR'],
    coverage: '95%',
    image: 'assets/ship-105596_1280.jpg'
  },
  {
    region: 'Midwest',
    states: ['IL', 'IN', 'OH', 'MI', 'WI', 'MN', 'IA', 'MO', 'KS', 'NE', 'ND', 'SD'],
    coverage: '97%',
    image: 'assets/warehouse-8921538_1280.jpg'
  },
  {
    region: 'Southwest',
    states: ['TX', 'OK', 'NM', 'AZ', 'CO', 'UT', 'NV'],
    coverage: '93%',
    image: 'assets/truck-1910871_1280.jpg'
  },
  {
    region: 'West Coast',
    states: ['CA', 'OR', 'WA', 'ID', 'MT', 'WY'],
    coverage: '96%',
    image: 'assets/airplane-4974678_1280.jpg'
  }
];

const technologyFeatures = [
  {
    icon: Zap,
    title: 'Real-Time Visibility',
    description: 'Track shipments across our entire network with live updates and predictive analytics'
  },
  {
    icon: Shield,
    title: 'Advanced Security',
    description: 'Multi-layered security systems with 24/7 monitoring and access control'
  },
  {
    icon: Users,
    title: 'Smart Routing',
    description: 'AI-powered route optimization for maximum efficiency and cost savings'
  },
  {
    icon: Globe,
    title: 'Global Integration',
    description: 'Seamless integration with international partners and customs systems'
  }
];

export default function Network() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Network & Infrastructure — CROSSDOCS</title>
        <meta name="description" content="Explore CROSSDOCS nationwide network of 25+ strategic hubs, advanced technology infrastructure, and comprehensive coverage across 48 states." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container-consistent text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Nationwide Network
              <span className="block text-primary-400">Strategic Infrastructure</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Our strategically positioned network spans 48 states with 25+ hubs, 
              providing comprehensive coverage and lightning-fast response times.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Network Stats */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="container-consistent">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {networkStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategic Hubs */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Strategic Hub Network
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Our strategically positioned hubs are designed for maximum efficiency, 
              providing rapid response times and comprehensive service coverage.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {networkHubs.map((hub, index) => (
              <motion.div
                key={hub.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={hub.image}
                    alt={hub.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-1">{hub.name}</h3>
                      <p className="text-slate-600">{hub.location}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-slate-500">Capacity</div>
                      <div className="font-semibold text-slate-900">{hub.capacity}</div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-slate-900 mb-2">Services:</h4>
                    <div className="flex flex-wrap gap-2">
                      {hub.services.map((service) => (
                        <span
                          key={service}
                          className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-slate-900 mb-2">Key Features:</h4>
                    <ul className="space-y-1">
                      {hub.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-primary-600 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="text-center">
                    <div className="text-sm text-slate-500 mb-1">Daily Throughput</div>
                    <div className="text-lg font-bold text-slate-900">{hub.throughput}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Comprehensive Coverage
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Our network provides extensive coverage across all major regions, 
              ensuring fast, reliable service wherever your business operates.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {coverageAreas.map((area, index) => (
              <motion.div
                key={area.region}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200"
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={area.image}
                    alt={area.region}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-slate-900">{area.region}</h3>
                    <span className="text-2xl font-bold text-primary-600">{area.coverage}</span>
            </div>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-slate-900 mb-2">States Covered:</h4>
                    <div className="flex flex-wrap gap-1">
                      {area.states.map((state) => (
                        <span
                          key={state}
                          className="inline-block px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded"
                        >
                          {state}
                        </span>
          ))}
        </div>
      </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Infrastructure */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Advanced Technology Infrastructure
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our network is powered by cutting-edge technology that provides 
                real-time visibility, predictive analytics, and seamless integration.
              </p>
              
              <div className="space-y-6">
                {technologyFeatures.map((feature, index) => (
                  <div key={feature.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                      <p className="text-slate-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Network Benefits</h3>
              
              <div className="space-y-4">
                {[
                  'Reduced transit times by 30-40%',
                  'Lower costs through route optimization',
                  'Real-time tracking and visibility',
                  'Predictive analytics for planning',
                  'Seamless integration with your systems',
                  '24/7 monitoring and support'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary-600 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
        <div className="container-consistent text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Access Our Network?
            </h2>
            <p className="text-lg text-primary-100 max-w-3xl mx-auto mb-8">
              Join hundreds of companies already leveraging our nationwide network 
              for faster, more efficient logistics operations.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl bg-white px-8 py-4 text-base font-semibold text-primary-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Get Started
              </NavLink>
              <NavLink 
                to="/services" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-base font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                Learn More
              </NavLink>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}






